/**
 * Event Builder — GET responses → reducer state (plan 9.1 / 9.3).
 *
 * Each function returns a partial state suitable for `actions.hydrate(...)` and never marks a
 * step dirty. The mapping mirrors what the four old wizard pages do when they load an event.
 */

import moment from "moment";
import isEmpty from "../../../../validation/is-empty";
import { canUseOrganizationStaff, hasPooledAvailability } from "../../../../utils/organizationStaffAccess";
import {
  ASSIGNMENT_MODE,
  D,
  DEFAULT_COLOR,
  DEFAULT_QUESTION,
  DURATION_PRESETS,
  E,
  FLAT_PERCENT,
  KIND,
  PAYMENT_TYPE,
  SCHEDULE_SOURCE,
  SCHEDULE_TYPE,
  STAFF_ASSIGNMENT_TYPE,
  STAFF_CHOOSE,
  VALIDITY,
  VENUE_LOCATION_STATUS,
  VENUE_OTHER_DISPLAY_STATUS,
  INVITEE_CALL_STATUS,
} from "./constants";
import { defaultAdvanced, defaultDetailsFree, defaultDetailsPaid } from "./builderReducer";

const str = (v) => (v === null || v === undefined ? "" : String(v));
const intOrZero = (v) => parseInt(v, 10) || 0;
const flag = (v, fallback = D) => (v === E ? E : v === D ? D : fallback);

/** Today's type page auto-slug (add-new-event.jsx): spaces → '-', lower-cased, nothing else. */
export const slugify = (name) => String(name || "").replace(/\s/g, "-").toLowerCase();

// ---- Type page (admin/event/event-type-page/{id}) ------------------------------------------------------

export function hydrateType(response) {
  const r = response || {};
  const isPaid = r.is_event_paid === E;
  return {
    meta: {
      eventId: parseInt(r.id, 10) || null,
      isNew: false,
      kind: isPaid ? KIND.PAID : KIND.FREE,
      isActive: r.is_active === E ? E : D,
      // backend 7.3 adds `is_draft`; until deployed an existing event is treated as published (safe:
      // the advanced payload then omits keep_draft, exactly what the old wizard does)
      isDraft: r.is_draft === E ? E : D,
    },
    lookups: { loadedIsPaid: isPaid },
    type: {
      event_name: str(r.event_name),
      event_url: str(r.event_url),
      // 3.5: a draft keeps auto-slugging while its URL still equals slug(name) (today's type page
      // behaviour); a hand-edited URL is left alone. Published events are frozen by `meta.isDraft`.
      urlTouched: str(r.event_url) !== slugify(str(r.event_name)),
      event_format_id: str(r.event_format_id || "1"),
      no_of_attendies: intOrZero(r.no_of_attendies) || 1,
      venue_id: str(r.venue_id),
      venue_location: str(r.venue_location),
      venue_location_notes: str(r.venue_location_notes),
      venue_location_status: str(r.venue_location_status) || VENUE_LOCATION_STATUS.DISPLAY_BOOKING,
      invitee_call_status: str(r.invitee_call_status) || INVITEE_CALL_STATUS.ME,
      host_phone_no: str(r.host_phone_no),
      venue_other_notes: str(r.venue_other_notes),
      venue_other_display_status: str(r.venue_other_display_status) || VENUE_OTHER_DISPLAY_STATUS.BOOKING_NOTES,
    },
  };
}

// ---- Details page ({event|paid-event}-details-page/{id}) ------------------------------------------------

/** Inverse of the 6.H mapping table — exactly add-new-event-details.jsx lines 218–238. */
export function paymentsFromRows(payments) {
  const out = {
    payments_type: "",
    payments_price: "",
    payments_flat_type: FLAT_PERCENT.PERCENT,
    deposit_type: "",
    deposit_price: "",
    deposit_flat_per_type: FLAT_PERCENT.PERCENT,
  };
  (Array.isArray(payments) ? payments : []).forEach((payment) => {
    if (payment.prepayment_type === PAYMENT_TYPE.DEPOSIT) {
      out.deposit_type = payment.prepayment_type;
      out.deposit_price = str(payment.prepayment_type_price);
      out.deposit_flat_per_type = payment.prepayment_type_flat_percent || FLAT_PERCENT.PERCENT;
    } else {
      out.payments_type = str(payment.prepayment_type);
      out.payments_price = str(payment.prepayment_type_price);
      out.payments_flat_type = payment.prepayment_type_flat_percent || FLAT_PERCENT.PERCENT;
    }
  });
  // legacy: a deposit row only (or no rows) → treated as full payment on the next save (6.H)
  if (out.payments_type === "") out.payments_type = PAYMENT_TYPE.FULL;
  return out;
}

const lineItems = (items, defaultQty) =>
  (Array.isArray(items) ? items : []).map((it) => ({
    item_name: str(it.item_name),
    price: str(it.price),
    qty: intOrZero(it.qty) || defaultQty,
  }));

export function hydrateDetails(response, kind, eventName = "") {
  const r = response || {};
  const shared = {
    event_image: null,
    event_image_url: str(r.event_image),
    event_color: str(r.event_color) || DEFAULT_COLOR,
    event_description: str(r.event_description),
    max_no_booking_status: flag(r.max_no_booking_status),
    max_no_of_booking: intOrZero(r.max_no_of_booking),
    recurring_booking_status: flag(r.recurring_booking_status),
    allow_rescheduling_status: flag(r.allow_rescheduling_status, E),
    attendee_cancellation_status: flag(r.attendee_cancellation_status, E),
    cancellation_policy: str(r.cancellation_policy),
    instruction_to_attendee: str(r.instruction_to_attendee),
    ita_booking_page_status: flag(r.ita_booking_page_status),
    ita_confirmation_page_status: flag(r.ita_confirmation_page_status),
    ita_reminder_email_status: flag(r.ita_reminder_email_status),
  };

  if (kind !== KIND.PAID) {
    return { details: { ...defaultDetailsFree(), ...shared } };
  }

  const pricing = lineItems(r.pricing, 1);
  return {
    details: {
      ...defaultDetailsPaid(eventName),
      ...shared,
      pricing: pricing.length ? pricing : [{ item_name: eventName, price: "", qty: 1 }],
      // UI-only: pricing[0].item_name follows the event name until it was edited (6.C block 7)
      pricingNameTouched: pricing.length > 0 && str(pricing[0].item_name) !== str(eventName),
      add_ons: lineItems(r.add_ons, 0),
      event_prepaid_status: flag(r.event_prepaid_status),
      ...paymentsFromRows(r.payments),
      coupon_codes_status: flag(r.coupon_codes_status),
      recurrent_booking_discounts_status: flag(r.recurrent_booking_discounts_status),
      staff_commission_status: flag(r.staff_commission_status),
      staff_commission_rate: intOrZero(r.staff_commission_rate),
      // keep '' for older paid events (today's select shows its placeholder); never coerce to 'none'
      refund_type: str(r.refund_type),
      refund_percentage: str(r.refund_percentage),
      refund_cutoff_time: r.refund_cutoff_time === null || r.refund_cutoff_time === undefined ? "" : str(r.refund_cutoff_time),
    },
  };
}

// ---- Availability page (event-availability-page/{id}) -------------------------------------------------

/**
 * Ported from add-new-event-availbility.jsx `normalizeEventDate`.
 * Accepts YYYY-MM-DD, MM/DD/YYYY, DD-MM-YYYY from older rows → ISO YYYY-MM-DD.
 */
export function normalizeEventDate(value) {
  if (isEmpty(value)) return "";
  const parsed = moment(value, ["YYYY-MM-DD", "MM/DD/YYYY", "DD-MM-YYYY", "DD/MM/YYYY", "M/D/YYYY"], true);
  if (parsed.isValid()) return parsed.format("YYYY-MM-DD");
  const fallback = moment(value);
  return fallback.isValid() ? fallback.format("YYYY-MM-DD") : "";
}

const datedToState = (dated) => {
  const m = moment(str(dated), ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD", "DD-MM-YYYY"]);
  return m.isValid() ? m.format("DD-MM-YYYY") : str(dated);
};

const availabilityRows = (rows) =>
  (Array.isArray(rows) ? rows : []).map((s) => ({ start_time: str(s.start_time), end_time: str(s.end_time) }));

/** Ported from `processSchedulesForListView` (weekly vs custom, `DD-MM-YYYY` dated). */
export function processSchedulesForListView(schedules) {
  return (Array.isArray(schedules) ? schedules : []).map((schedule) => ({
    type: schedule.schedule_type,
    staffId: schedule.staff_id === null || schedule.staff_id === undefined ? 0 : schedule.staff_id,
    timezoneId: str(schedule.timezone_id),
    schedules: (schedule.schedule_details || [])
      .map((detail) => {
        if (detail.type === "weekly" || (detail.weekday_id !== null && detail.weekday_id !== undefined)) {
          return { dayId: parseInt(detail.weekday_id, 10), dated: null, schedules: availabilityRows(detail.schedule_availability) };
        }
        if (detail.type === "custom") {
          return { dayId: null, dated: datedToState(detail.dated), schedules: availabilityRows(detail.schedule_availability) };
        }
        return null;
      })
      .filter(Boolean),
  }));
}

/** get-schedule/{businessId}/{scheduleId} → rows for `availability.schedules[type=event].schedules` (Event-Availbility-Component.getTemplateSchedule). */
export function templateToRows(scheduleData) {
  const d = scheduleData || {};
  const rows = (d.schedule_details || [])
    .map((detail) => {
      if (detail.type === "weekly") {
        return { dayId: parseInt(detail.weekday_id, 10), dated: null, schedules: availabilityRows(detail.schedule_availability) };
      }
      if (detail.type === "custom") {
        return { dayId: null, dated: datedToState(detail.dated), schedules: availabilityRows(detail.schedule_availability) };
      }
      return null;
    })
    .filter(Boolean);
  return { rows, timezoneId: str(d.timezone_id) };
}

export const durationPresetFor = (minutes) => {
  const n = parseInt(minutes, 10);
  return DURATION_PRESETS.includes(n) ? n : "custom";
};

/**
 * 9.3 assignmentMode: type 1 → my; type 2 + random_choose/method → auto;
 * type 2 + allow_invitee + 1 staff → assign; >1 → invitee.
 */
export function assignmentModeFrom({ staffAssignmentType, allowAttendeeSelectStaff, staffAssignmentMethod, staffCount }) {
  if (String(staffAssignmentType) !== STAFF_ASSIGNMENT_TYPE.STAFF) return ASSIGNMENT_MODE.MY;
  if (allowAttendeeSelectStaff === E) return staffCount <= 1 ? ASSIGNMENT_MODE.ASSIGN : ASSIGNMENT_MODE.INVITEE;
  if (staffAssignmentMethod) return ASSIGNMENT_MODE.AUTO;
  return staffCount <= 1 ? ASSIGNMENT_MODE.ASSIGN : ASSIGNMENT_MODE.INVITEE;
}

/**
 * @param response  availability GET
 * @param ctx       { userPlan, features, scheduleTemplates? } — plan gates applied exactly as the old page's loader
 */
export function hydrateAvailability(response, { userPlan, features } = {}) {
  const r = response || {};
  const canUseStaff = canUseOrganizationStaff(userPlan);
  const hasPooled = hasPooledAvailability(features);

  const staffAssignmentType = canUseStaff && r.staff_assignment_type ? String(r.staff_assignment_type) : STAFF_ASSIGNMENT_TYPE.MY_EVENT;
  const eventStaffs = canUseStaff && Array.isArray(r.event_staffs) ? r.event_staffs : [];
  const staffs = eventStaffs.map((s) => parseInt(s.id, 10)).filter((n) => !Number.isNaN(n));

  let staffChoose = "";
  if (canUseStaff) {
    if (r.allow_attendee_select_staff === E) staffChoose = STAFF_CHOOSE.ALLOW_INVITEE;
    else if (hasPooled && r.staff_assignment_method) staffChoose = STAFF_CHOOSE.RANDOM;
  }
  const method = canUseStaff && hasPooled ? str(r.staff_assignment_method) : "";

  const schedules = processSchedulesForListView(r.schedules);
  const eventRow = schedules.find((s) => s.type === SCHEDULE_TYPE.EVENT);
  const staffRows = canUseStaff ? schedules.filter((s) => s.type === SCHEDULE_TYPE.EVENT_STAFF) : [];

  const validity = [VALIDITY.INDEFINITE, VALIDITY.VALID_OVER, VALIDITY.DATE_RANGE].includes(r.event_validity)
    ? r.event_validity
    : VALIDITY.INDEFINITE; // old page leaves '' and requires a choice; server defaults unknown → indefinitly

  return {
    availability: {
      event_duration_in_minutes: intOrZero(r.event_duration_in_minutes),
      invitee_set_duration_status: flag(r.invitee_set_duration_status),
      is_time_block: D,
      event_internal_notes: str(r.event_internal_notes),
      max_event_per_week: intOrZero(r.max_event_per_week),
      max_event_per_day: intOrZero(r.max_event_per_day),
      buffer_before_event_minutes: intOrZero(r.buffer_before_event_minutes),
      max_scheduling_notice_minutes: intOrZero(r.max_scheduling_notice_minutes),
      min_scheduling_notice_minutes: intOrZero(r.min_scheduling_notice_minutes),
      buffer_after_event_minutes: intOrZero(r.buffer_after_event_minutes),
      slot_duration_increment_of: intOrZero(r.slot_duration_increment_of),
      staff_assignment_type: staffAssignmentType,
      staff_choose: staffChoose,
      staff_assignment_method: method,
      assignmentMode: assignmentModeFrom({
        staffAssignmentType,
        allowAttendeeSelectStaff: canUseStaff ? r.allow_attendee_select_staff : D,
        staffAssignmentMethod: method,
        staffCount: staffs.length,
      }),
      staffs,
      event_validity: validity,
      valid_over_period: str(r.valid_over_period),
      event_start_date: normalizeEventDate(r.event_start_date),
      event_end_date: normalizeEventDate(r.event_end_date),
      timezone_id: eventRow ? eventRow.timezoneId : "",
      // an existing event's rows are its own (copied from a template at creation): edit them as custom
      scheduleSource: eventRow ? SCHEDULE_SOURCE.CUSTOM : SCHEDULE_SOURCE.TEMPLATE,
      templateId: "",
      schedules: [...(eventRow ? [eventRow] : []), ...staffRows],
    },
    lookups: {
      datePickerFormat: r.date_picker_format || "mm/dd/yyyy",
      calendarFirstDay: str(r.calendar_first_day || "1"),
      hadRoundRobin: canUseStaff && hasPooled && r.staff_assignment_method === "round-robin",
      eventStaffs, // [{id, first_name, last_name}] — keeps staff no longer in the active list visible (6.E)
    },
  };
}

// ---- Advanced page (event-advanced-page/{id}) ------------------------------------------------------------

export function hydrateAdvanced(response) {
  const r = response || {};
  const org = r.org_staff_can_confirm_booking === E ? E : D;
  const questions = (Array.isArray(r.questions) ? r.questions : []).map((q) => ({
    question: str(q.question),
    question_type: str(q.question_type) || "text",
    show_status: flag(q.show_status, E),
    mandatory_status: flag(q.mandatory_status),
    // the GET returns options under `question_details` (old page: question.details = question.question_details)
    details: (q.question_details || q.details || []).map((d) => ({
      field_label: str(d.field_label),
      field_value: str(d.field_value),
    })),
  }));

  return {
    advanced: {
      ...defaultAdvanced(),
      event_public_visiblity_status: flag(r.event_public_visiblity_status, E),
      confirmation_page: r.confirmation_page === "external" ? "external" : "default",
      confirmation_page_url: str(r.confirmation_page_url),
      customer_feedback_status: flag(r.customer_feedback_status),
      email_reminder_status: flag(r.email_reminder_status),
      sms_reminder_status: flag(r.sms_reminder_status),
      email_followup_status: flag(r.email_followup_status),
      invitee_guests_status: flag(r.invitee_guests_status),
      staff_can_confirm_booking: org === E ? flag(r.staff_can_confirm_booking) : D,
      questions: questions.length ? questions : [{ ...DEFAULT_QUESTION, details: [] }],
    },
    lookups: {
      orgStaffCanConfirm: org,
      hadStaffConfirm: r.staff_can_confirm_booking === E,
    },
  };
}

// ---- Lookups ------------------------------------------------------------------------------------------------

/** GET admin/staffs/{businessId} → today's filter (add-new-event-availbility.jsx componentDidMount). */
export const filterActiveStaff = (list) =>
  (Array.isArray(list) ? list : []).filter(
    (s) =>
      s.status === E &&
      // eslint-disable-next-line eqeqeq
      s.user_account_status == "active" &&
      // eslint-disable-next-line eqeqeq
      s.is_staff_acc_deleted == D &&
      // eslint-disable-next-line eqeqeq
      s.is_deleted == D
  );

/** Company templates for the event schedule select (Event-Availbility-Component.loadScheduleTemplates, type 'event'). */
export const companyTemplates = (templates) =>
  (Array.isArray(templates) ? templates : []).filter((t) => t.schedule_type === "company");

/** Default template: is_business_hours 'E', else is_default 'E' (same component). */
export const defaultCompanyTemplate = (templates) => {
  const list = companyTemplates(templates);
  return list.find((t) => t.is_business_hours === E) || list.find((t) => t.is_default === E) || null;
};

/** Staff template options (type 'event_staff'): own templates + company default/business-hours. */
export const staffTemplates = (templates, staffId) =>
  (Array.isArray(templates) ? templates : []).filter(
    (t) =>
      parseInt(t.staff_id, 10) === parseInt(staffId, 10) ||
      (t.schedule_type === "company" && (t.is_default === E || t.is_business_hours === E))
  );

export const defaultStaffTemplate = (templates, staffId) =>
  (Array.isArray(templates) ? templates : []).find(
    (t) => t.is_default === E && parseInt(t.staff_id, 10) === parseInt(staffId, 10)
  ) || null;
